import React from 'react';
import { XCircle, AlertTriangle, RefreshCw } from 'lucide-react';

const MncapMedicalError: React.FC = () => {
  const handleRetry = () => {
    window.history.back();
  };

  const handleContact = () => {
    // Logique de contact
    window.location.href = 'mailto:contact@mncap.fr';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-primary/5 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Cercles décoratifs animés */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-red-100 rounded-full animate-pulse opacity-50" />
      <div className="absolute bottom-32 right-20 w-32 h-32 bg-primary/10 rounded-full animate-pulse opacity-50" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 right-10 w-16 h-16 bg-secondary/10 rounded-full animate-pulse opacity-50" style={{ animationDelay: '2s' }} />

      {/* Contenu principal */}
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 relative overflow-hidden">
          {/* Décoration de fond */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-red-50 rounded-full -mr-16 -mt-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-primary/5 rounded-full -ml-12 -mb-12" />

          {/* Contenu */}
          <div className="relative z-10">
            {/* Icône d'erreur */}
            <div className="flex justify-center mb-6">
              <div className="bg-red-50 rounded-full p-4 animate-shake">
                <XCircle className="w-16 h-16 text-red-500" strokeWidth={2} />
              </div>
            </div>

            {/* Titre */}
            <h1 className="text-3xl font-bold text-center text-gray-800 mb-4">
              Une erreur est survenue
            </h1>

            {/* Message principal */}
            <div className="bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 mb-6">
              <p className="text-gray-700 text-center leading-relaxed">
                Votre formulaire médical n'a pas pu être transmis à la MNCAP.
              </p>
            </div>

            {/* Causes possibles */}
            <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg p-4 mb-6">
              <div className="flex items-center space-x-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-primary" />
                <h2 className="font-semibold text-gray-800">Causes possibles :</h2>
              </div>
              <div className="space-y-2">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-gray-600">
                    Problème de connexion internet
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-gray-600">
                    Session expirée
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-gray-600">
                    Données incomplètes ou invalides
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-secondary rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-gray-600">
                    Problème technique temporaire
                  </p>
                </div>
              </div>
            </div>

            {/* Conseils */}
            <div className="bg-primary/5 rounded-lg p-4 mb-6 border border-primary/20">
              <h3 className="font-semibold text-gray-800 mb-2 text-sm">
                Que faire maintenant ?
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                Vérifiez votre connexion internet, puis réessayez de soumettre le formulaire.
                Si le problème persiste, n'hésitez pas à contacter notre support.
              </p>
            </div>

            {/* Boutons d'action */}
            <div className="space-y-3">
              <button
                onClick={handleRetry}
                className="w-full bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center space-x-2"
              >
                <RefreshCw className="w-5 h-5" />
                <span>Réessayer</span>
              </button>

              <button
                onClick={handleContact}
                className="w-full bg-gradient-to-r from-secondary to-secondary/90 hover:from-secondary/90 hover:to-secondary text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                Contacter le support
              </button>

              <button
                onClick={() => window.location.href = '/'}
                className="w-full bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-all duration-300 border-2 border-gray-200 hover:border-gray-300"
              >
                Retour à l'accueil
              </button>
            </div>
          </div>
        </div>

        {/* Information de support */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Besoin d'aide ? Appelez-nous au{' '}
            <span className="font-semibold text-primary">01 XX XX XX XX</span>
          </p>
        </div>
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% {
            transform: translateX(0);
          }
          10%, 30%, 50%, 70%, 90% {
            transform: translateX(-5px);
          }
          20%, 40%, 60%, 80% {
            transform: translateX(5px);
          }
        }

        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default MncapMedicalError;
